<template>
  <v-card flat tile class="d-flex flex-column gc-list-panel">
    <confirm ref="confirm"></confirm>
    <save-diff-modal ref="saveDiff"></save-diff-modal>
    <rename-modal ref="renameModal"></rename-modal>
    <move-modal ref="moveModal"></move-modal>
    <div class="gc-list-scroll">
    <v-card-text
        v-if="!path"
        class="grow d-flex justify-center align-center grey--text"
    >Select a folder or a file</v-card-text>
    <v-card-text v-else-if="isFile" class="gc-file-view" :class="fileViewClass">
      <asset-url-bar :url="assetUrl" block @copy="copyAssetUrl" />

      <div class="gc-file-view__body" :class="fileBodyClass">
        <template v-if="isImage">
          <div class="gc-media-viewer__header">
            <span class="gc-media-viewer__filename" :title="fileName">{{ fileName }}</span>
            <v-btn
              class="gc-media-viewer__open-btn"
              depressed
              small
              tag="a"
              :href="assetUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <v-icon left small>mdi-open-in-new</v-icon>
              Open original
            </v-btn>
          </div>
          <div class="gc-media-viewer__stage">
            <v-img
              :src="formUrl(path, true)"
              :alt="fileName"
              contain
              class="gc-media-viewer__img"
            >
              <template v-slot:placeholder>
                <div class="gc-media-viewer__loading">
                  <v-progress-circular indeterminate color="#f5c347" size="40" />
                </div>
              </template>
            </v-img>
          </div>
        </template>

        <template v-else-if="isVideo">
          <div class="gc-media-viewer__header">
            <span class="gc-media-viewer__filename" :title="fileName">{{ fileName }}</span>
            <v-btn
              class="gc-media-viewer__open-btn"
              depressed
              small
              tag="a"
              :href="assetUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <v-icon left small>mdi-open-in-new</v-icon>
              Open original
            </v-btn>
          </div>
          <div class="gc-media-viewer__stage gc-media-viewer__stage--video">
            <video
              class="gc-media-viewer__video"
              :src="formUrl(path)"
              controls
              playsinline
              preload="metadata"
            >
              Your browser does not support embedded video playback.
            </video>
          </div>
        </template>

        <template v-else-if="isTextEditable">
          <v-textarea
            v-model="content"
            dark
            outlined
            hide-details
            class="gc-text-editor"
            auto-grow
            :readonly="!canUpdate"
          />
          <v-btn
            v-if="canUpdate"
            class="save-editor-btn"
            depressed
            :disabled="!hasUnsavedChanges"
            @click="save"
          >
            Save
            <span v-if="hasUnsavedChanges" class="save-editor-btn__dot" />
          </v-btn>
        </template>

        <template v-else-if="isJSON">
          <div v-if="showGenerateToolbar" class="gc-json-generate">
            <div class="gc-generate-toolbar">
              <div class="gc-translation-prompts-stack">
                <translation-restricted-terms-field
                  :value="translationRestrictedTerms"
                  :defaults="keepEnglishFeatures"
                  @input="onRestrictedTermsInput"
                />
                <div class="gc-translation-advanced">
                  <button
                    type="button"
                    class="gc-translation-advanced__toggle"
                    @click="translationAdvancedOpen = !translationAdvancedOpen"
                  >
                    {{ translationAdvancedOpen ? 'Hide' : 'Show' }} advanced (UI info config)
                  </button>
                  <div v-if="translationAdvancedOpen" class="gc-translation-prompts-row">
                    <translation-prompt-field
                      :value="translationUiInfoConfig"
                      class="gc-translation-prompts-row__field"
                      label="UI info config (optional)"
                      placeholder='Paste info/default.json — {"info":[...]}'
                      hint="Paste full info/default.json — server compacts it to key layout (drops URLs/images)."
                      :rows="3"
                      @input="onTranslationUiInfoInput"
                    />
                  </div>
                </div>
              </div>
              <div class="gc-generate-toolbar__controls">
                <translation-model-select />
                <div class="gc-generate-toolbar__select-wrap">
                  <div class="gc-generate-toolbar__select-header">
                    <span class="gc-generate-toolbar__select-label">Target languages</span>
                    <button
                      type="button"
                      class="gc-generate-toolbar__link"
                      :disabled="!generateTargetLocaleOptions.length || allGenerateLocalesSelected"
                      @click="selectAllGenerateLocales"
                    >
                      Select all
                    </button>
                    <button
                      v-if="!translateSpecificKeys"
                      type="button"
                      class="gc-generate-toolbar__link"
                      :disabled="!canAddMissingGenerateLocales || allMissingGenerateLocalesSelected"
                      @click="addMissingGenerateLocales"
                    >
                      Add missing
                    </button>
                    <button
                      v-if="generateTargetLocales.length"
                      type="button"
                      class="gc-generate-toolbar__link"
                      @click="clearGenerateLocales"
                    >
                      Clear
                    </button>
                  </div>
                  <v-select
                    v-model="generateTargetLocales"
                    :items="generateTargetLocaleOptions"
                    item-text="text"
                    item-value="value"
                    :placeholder="generateTargetLocalePlaceholder"
                    :loading="translateSpecificKeys && scanningGenerateLocales"
                    :disabled="translateSpecificKeys && !scanningGenerateLocales && !generateTargetLocaleOptions.length"
                    dense
                    outlined
                    hide-details
                    dark
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    class="gc-generate-toolbar__select"
                    :class="{ 'gc-generate-toolbar__select--has-value': generateTargetLocales.length }"
                  />
                </div>
                <div class="gc-generate-toolbar__specific-keys">
                  <v-checkbox
                    v-model="translateSpecificKeys"
                    class="gc-generate-toolbar__checkbox"
                    label="Translate specific keys"
                    dense
                    dark
                    hide-details
                    @change="onTranslateSpecificKeysChange"
                  />
                  <div
                    v-if="translateSpecificKeys"
                    class="gc-generate-toolbar__select-wrap gc-generate-toolbar__keys"
                  >
                    <div class="gc-generate-toolbar__select-header">
                      <span class="gc-generate-toolbar__select-label">Keys to translate</span>
                      <button
                        v-if="generateSelectedKeyPaths.length"
                        type="button"
                        class="gc-generate-toolbar__link"
                        @click="clearGenerateSelectedKeys"
                      >
                        Clear
                      </button>
                    </div>
                    <v-autocomplete
                      v-model="generateSelectedKeyPaths"
                      :items="generateKeyPathOptions"
                      item-text="text"
                      item-value="value"
                      :return-object="false"
                      placeholder="Select key paths from this EN file"
                      persistent-placeholder
                      dense
                      outlined
                      hide-details
                      dark
                      multiple
                      chips
                      small-chips
                      deletable-chips
                      clearable
                      class="gc-generate-toolbar__select"
                      :class="{ 'gc-generate-toolbar__select--has-value': generateSelectedKeyPaths.length }"
                      @change="onGenerateSelectedKeysChange"
                    />
                  </div>
                </div>
                <div class="gc-generate-toolbar__actions">
                  <v-btn
                    depressed
                    class="gc-generate-toolbar__btn"
                    :loading="generatingTranslation"
                    :disabled="!canGenerateTranslations"
                    @click="generateTargetTranslation"
                  >
                    Generate translations
                  </v-btn>
                </div>
              </div>
              <span class="gc-generate-toolbar__hint">
                <template v-if="generateProgress">{{ generateProgress }}</template>
                <translation-cost-hint
                  v-else-if="generateTranslationCostEstimate"
                  :estimate="generateTranslationCostEstimate"
                  :target-locale-count="generateTargetLocales.length"
                />
                <template v-else-if="translateSpecificKeys && generateSelectedKeyPaths.length">
                  {{ generateSelectedKeyPaths.length }} key(s) selected — Generate merges only those paths into existing locale files.
                </template>
                <template v-else-if="translateSpecificKeys">
                  Target languages are limited to locales that already exist. Select key paths, then Generate.
                </template>
                <template v-else>
                  Full-file Generate overwrites each selected locale. Enable Translate specific keys to merge a subset into existing files.
                </template>
              </span>
            </div>
            <vue-json-editor
              ref="jsonEditor"
              v-model="content"
              :show-btns="canUpdate"
              :mode="canUpdate ? 'tree' : 'view'"
              :expandedOnStart="true"
              @json-save="save"
            />
          </div>
          <div
            v-else-if="showTranslationSplit"
            class="gc-json-split"
            :class="{ 'gc-json-split--triple': comparisonOpen && !isSenFile }"
          >
            <div class="gc-json-split__toolbar">
              <translation-model-select class="gc-json-split__model-select" />
              <div class="gc-translation-prompts-stack gc-json-split__prompts">
                <translation-restricted-terms-field
                  :value="translationRestrictedTerms"
                  :defaults="keepEnglishFeatures"
                  @input="onRestrictedTermsInput"
                />
                <div class="gc-translation-advanced">
                  <button
                    type="button"
                    class="gc-translation-advanced__toggle"
                    @click="translationAdvancedOpen = !translationAdvancedOpen"
                  >
                    {{ translationAdvancedOpen ? 'Hide' : 'Show' }} advanced (UI info config)
                  </button>
                  <div v-if="translationAdvancedOpen" class="gc-translation-prompts-row">
                    <translation-prompt-field
                      :value="translationUiInfoConfig"
                      class="gc-translation-prompts-row__field"
                      label="UI info config (optional)"
                      placeholder='Paste info/default.json — {"info":[...]}'
                      hint="Paste full info/default.json — server compacts it to key layout (drops URLs/images)."
                      :rows="3"
                      @input="onTranslationUiInfoInput"
                    />
                  </div>
                </div>
              </div>
              <v-btn
                depressed
                class="gc-json-split__btn"
                :loading="comparingEnglish"
                :disabled="comparingEnglish || !isCompareTranslationEnabled"
                @click="runEnglishComparison"
              >
                Compare with English
              </v-btn>
              <v-btn
                v-if="comparisonOpen && comparisonStale"
                depressed
                outlined
                small
                class="gc-json-split__btn gc-json-split__btn--secondary"
                :loading="comparingEnglish"
                :disabled="comparingEnglish"
                @click="refreshEnglishComparison"
              >
                Refresh translation
              </v-btn>
              <v-btn
                v-if="canUpdate"
                depressed
                class="save-editor-btn gc-json-split__save"
                :disabled="!hasUnsavedChanges"
                @click="save"
              >
                Save
                <span v-if="hasUnsavedChanges" class="save-editor-btn__dot" />
              </v-btn>
              <span
                v-if="comparisonOpen && englishComparisonScore"
                class="gc-json-split__accuracy"
                :class="`gc-json-split__accuracy--${englishAccuracyLevel}`"
                :title="englishAccuracyTitle"
              >
                <span class="gc-json-split__accuracy-label">
                  vs en.json
                  <span
                    v-if="englishAccuracyLevelLabel"
                    class="gc-json-split__accuracy-signal"
                  >{{ englishAccuracyLevelLabel }}</span>
                </span>
                <span class="gc-json-split__accuracy-value">
                  <template v-if="englishComparisonScore.totalWords">
                    {{ englishComparisonScore.percent }}%
                  </template>
                  <template v-else>N/A</template>
                </span>
                <span class="gc-json-split__accuracy-detail">
                  <template v-if="englishComparisonScore.totalWords">
                    {{ englishComparisonScore.matchedWords }}/{{ englishComparisonScore.totalWords }}
                    words
                  </template>
                  <template v-else>en.json empty</template>
                </span>
              </span>
              <span
                v-else-if="comparisonOpen && englishAccuracyUnavailable"
                class="gc-json-split__accuracy gc-json-split__accuracy--unavailable"
                :title="englishAccuracyUnavailable"
              >
                Score unavailable
              </span>
              <span
                v-else-if="comparisonOpen && comparingEnglish"
                class="gc-json-split__accuracy gc-json-split__accuracy--pending"
              >
                Scoring…
              </span>
              <span class="gc-json-split__toolbar-hint">
                <template v-if="comparisonOpen && isSenFile">
                  Accuracy: sen.json vs
                  <strong v-if="englishReferencePath">{{ englishReferencePath }}</strong>
                  <template v-else>en.json</template>
                  (word match). Diff words highlighted; not saved.
                </template>
                <template v-else-if="comparisonOpen">
                  Accuracy: {{ sourceLocaleLabel }} → EN (Google Translate) vs
                  <strong v-if="englishReferencePath">{{ englishReferencePath }}</strong>
                  <template v-else>en.json</template>.
                  Diff words highlighted; not saved.
                </template>
                <translation-cost-hint
                  v-else-if="singlePassTranslationEstimate"
                  :estimate="singlePassTranslationEstimate"
                />
                <template v-else>
                  Compare previews EN without saving. Do not translate terms apply on Generate.
                </template>
              </span>
            </div>
            <div class="gc-json-split__body">
              <div class="gc-json-split__pane gc-json-split__pane--edit">
                <div v-if="comparisonOpen" class="gc-json-split__header">
                  <div class="gc-json-split__header-text">
                    <span class="gc-json-split__title">{{ sourceLocaleLabel }} (editing)</span>
                    <span class="gc-json-split__subtitle">{{ fileName }}</span>
                  </div>
                </div>
                <div
                  class="gc-json-split__editor-wrap"
                  :class="{ 'gc-json-split__preview-body': comparisonOpen }"
                >
                  <vue-json-editor
                    ref="jsonEditor"
                    v-model="content"
                    :show-btns="false"
                    :mode="canUpdate ? 'tree' : 'view'"
                    :expandedOnStart="true"
                    @json-save="save"
                  />
                </div>
              </div>
              <translation-preview-pane
                v-if="comparisonOpen && !isSenFile"
                ref="translationPreview"
                :active="comparisonOpen"
                :trigger="compareTrigger"
                :content="comparePreviewContent"
                :compare-diff="englishComparisonDiff"
                :source-locale="sourceLocale"
                :additional-prompt="''"
                :ui-info-config="translationUiInfoConfig"
                :translation-model="selectedTranslationModel"
                :translation-model-label="translationModelLabel"
                :axios="axios"
                @translating="onAiTranslating"
                @completed="onTranslationCompleted"
                @error="onTranslationPreviewError"
                @preview-ready="updateEnglishComparisonScore"
                @ready="onComparePaneReady"
              />
              <english-reference-pane
                v-if="comparisonOpen"
                ref="englishReference"
                :active="comparisonOpen"
                :trigger="compareTrigger"
                :compare-diff="englishComparisonDiff"
                :resolved-path="englishReferencePath"
                :storage="storage"
                :endpoints="endpoints"
                :axios="axios"
                @reference-ready="updateEnglishComparisonScore"
                @ready="onComparePaneReady"
              />
            </div>
          </div>
          <vue-json-editor
            v-else
            ref="jsonEditor"
            v-model="content"
            :show-btns="canUpdate"
            :mode="canUpdate ? 'tree' : 'view'"
            :expandedOnStart="true"
            @json-save="save"
          />
        </template>

        <template v-else>
          <div class="gc-file-preview">
            <v-icon class="gc-file-preview__icon" large>mdi-file-outline</v-icon>
            <p class="gc-file-preview__name">{{ fileName }}</p>
            <p class="gc-file-preview__hint">
              Preview not available for this file type.
            </p>
            <v-btn
              class="gc-file-preview__open"
              depressed
              small
              tag="a"
              :href="assetUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <v-icon left small>mdi-open-in-new</v-icon>
              Open / download
            </v-btn>
          </div>
        </template>
      </div>
    </v-card-text>
    <v-card-text v-else-if="dirs.length || files.length" class="grow">
      <div v-if="showAccuracyAuditButton" class="gc-locale-audit-bar">
        <v-btn
          depressed
          small
          class="gc-locale-audit-bar__btn"
          :loading="accuracyAuditing"
          :disabled="accuracyAuditing"
          @click="runAccuracyAudit"
        >
          <v-icon left small>mdi-chart-timeline-variant</v-icon>
          Accuracy Audit
        </v-btn>
        <span v-if="accuracyAuditProgress" class="gc-locale-audit-bar__progress">
          {{ accuracyAuditProgress }}
        </span>
        <span v-else-if="accuracyAuditSummary" class="gc-locale-audit-bar__hint">
          {{ accuracyAuditSummary }}
        </span>
      </div>
      <v-list subheader v-if="dirs.length">
        <v-subheader>Folders</v-subheader>
        <v-list-item
            v-for="item in dirs"
            :key="item.path"
            @click="changePath(item.path)"
            class="pl-0"
        >
          <v-list-item-avatar class="ma-0">
            <v-icon>mdi-folder-outline</v-icon>
          </v-list-item-avatar>
          <v-list-item-content class="py-2">
            <v-list-item-title v-text="item.basename"></v-list-item-title>
          </v-list-item-content>
          <v-list-item-action v-if="itemAuditScore(item)" class="gc-list-item__audit-wrap">
            <span
              class="gc-list-item__audit"
              :class="`gc-list-item__audit--${itemAuditScore(item).level}`"
              :title="itemAuditTitle(item)"
            >
              <template v-if="itemAuditScore(item).percent != null">
                {{ itemAuditScore(item).percent }}%
              </template>
              <template v-else>—</template>
            </span>
          </v-list-item-action>
          <v-list-item-action v-if="canDelete">
            <v-btn icon @click.stop="deleteItem(item)">
              <v-icon color="grey lighten-1">mdi-delete-outline</v-icon>
            </v-btn>
            <v-btn icon v-if="false">
              <v-icon color="grey lighten-1">mdi-information</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <v-divider v-if="dirs.length && files.length"></v-divider>
      <v-list v-if="files.length" subheader>
        <v-subheader class="gc-list-select-header">
          <v-checkbox
            :input-value="allListSelected"
            :indeterminate="someListSelected && !allListSelected"
            hide-details
            dense
            color="#f5c347"
            class="gc-list-select-header__checkbox"
            @click.stop
            @change="toggleSelectAll"
          />
          <span>Files</span>
        </v-subheader>
        <v-list-item
            v-for="item in files"
            :key="item.path"
            @click="changePath(item.path)"
            class="pl-0 gc-list-item"
            :class="{ 'gc-list-item--selected': isSelected(item.path) }"
        >
          <v-list-item-action class="gc-list-item__check ma-0">
            <v-checkbox
              :input-value="isSelected(item.path)"
              hide-details
              dense
              color="#f5c347"
              @click.stop="toggleSelect(item)"
            />
          </v-list-item-action>
          <v-list-item-avatar
            class="ma-0 gc-list-item__avatar"
            :class="{ 'gc-list-item__avatar--image': isImagePath(item.path) }"
          >
            <v-img
              v-if="isImagePath(item.path)"
              :src="formUrl(item.path, true)"
              :alt="item.basename"
              cover
              class="gc-list-item__thumb"
            >
              <template v-slot:placeholder>
                <v-icon>{{
                  icons[(item.extension || '').toLowerCase()] || icons.png
                }}</v-icon>
              </template>
              <template v-slot:error>
                <v-icon>{{
                  icons[(item.extension || '').toLowerCase()] || icons.png
                }}</v-icon>
              </template>
            </v-img>
            <v-icon v-else>{{
              icons[(item.extension || '').toLowerCase()] || icons['other']
            }}</v-icon>
          </v-list-item-avatar>

          <v-list-item-content class="py-2">
            <v-list-item-title v-text="item.basename"></v-list-item-title>
            <v-list-item-subtitle>{{ formatFileMeta(item) }}</v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action v-if="itemAuditScore(item)" class="gc-list-item__audit-wrap">
            <span
              class="gc-list-item__audit"
              :class="`gc-list-item__audit--${itemAuditScore(item).level}`"
              :title="itemAuditTitle(item)"
            >
              <template v-if="itemAuditScore(item).percent != null">
                {{ itemAuditScore(item).percent }}%
              </template>
              <template v-else>—</template>
            </span>
          </v-list-item-action>

          <v-list-item-action v-if="canDelete">
            <v-btn icon @click.stop="deleteItem(item)">
              <v-icon color="grey lighten-1">mdi-delete-outline</v-icon>
            </v-btn>
            <v-btn icon v-if="false">
              <v-icon color="grey lighten-1">mdi-information</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card-text>
    <v-card-text
      v-else-if="isDir && filter && !dirs.length && !files.length"
      class="grow d-flex justify-center align-center grey--text py-5"
    >
      No files or folders found
    </v-card-text>
    <v-card-text
      v-else-if="isDir && !dirs.length && !files.length"
      class="grow d-flex justify-center align-center grey--text py-5"
    >
      The folder is empty
    </v-card-text>
    </div>
    <v-divider v-if="path"></v-divider>
    <v-toolbar v-if="path && isDir" dense flat class="shrink gc-list-toolbar">
      <template v-if="hasSelection">
        <span class="gc-list-bulk__count">{{ selectedCount }} selected</span>
        <v-btn
          v-if="canUpdate"
          depressed
          small
          class="gc-list-bulk__btn"
          @click="moveSelected"
        >
          <v-icon left small>mdi-folder-arrow-left-right</v-icon>
          Move
        </v-btn>
        <v-btn
          v-if="canDelete"
          depressed
          small
          class="gc-list-bulk__btn gc-list-bulk__btn--danger"
          @click="deleteSelected"
        >
          <v-icon left small>mdi-delete-outline</v-icon>
          Delete
        </v-btn>
        <v-spacer />
        <v-btn icon small title="Clear selection" @click="clearSelection">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
      <template v-else>
        <v-text-field
            solo
            flat
            hide-details
            label="Filter"
            v-model="filter"
            prepend-inner-icon="mdi-filter-outline"
            class="ml-n3"
        ></v-text-field>
        <v-btn icon v-if="false">
          <v-icon>mdi-eye-settings-outline</v-icon>
        </v-btn>
        <v-btn icon @click="load">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </template>
    </v-toolbar>
  </v-card>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import vueJsonEditor from 'vue-json-editor';
import { formatBytes, formatFileMeta as buildFileMeta, imageUrl, isImagePath as checkImagePath } from '@/plugins/util';
import { editorContentEquals, serializeEditorContent } from '@/plugins/diff';
import {
  formatJsonTextForDiff,
  getJsonEditorText as readJsonEditorText,
  stringifyJsonObject
} from '@/plugins/json-editor-text';
import {
  COMMON_TARGET_LOCALES,
  SEN_GENERATE_OPTION,
  SEN_JSON,
  discoveryListPath,
  englishPathFromLocalePath,
  existingTargetLocalesFromListings,
  folderAccuracyAuditPlan,
  isEnglishLocale,
  isSenJsonPath,
  isTranslatableLocale,
  localeFromPath,
  nestedLocaleDirsToProbe,
  senPathFromEnglishPath,
  targetPathForLocale
} from '@/plugins/locale';
import {
  getJsonEditorScrollElement,
  linkJsonEditorScroll
} from '@/plugins/sync-json-editor-scroll';
import {
  collectStringLeaves,
  applyTranslations,
  mergeTranslations,
  filterEntriesByPaths,
  getValueAtPath,
  normalizeSelectedKeyPaths
} from '@/plugins/translation-json';
import {
  countTranslationCharacters,
  estimateTranslationCost
} from '@/plugins/translation-cost';
import {
  accuracyLevel,
  accuracyLevelLabel,
  ACCURACY_AMBER_MIN,
  ACCURACY_GREEN_MIN,
  buildEnglishComparisonDiff,
  scoreEnglishComparison
} from '@/plugins/english-comparison-score';
import TranslationCostHint from './TranslationCostHint.vue';
import TranslationPromptField, {
  loadStoredTranslationUiInfoConfig,
  saveStoredTranslationUiInfoConfig
} from './TranslationPromptField.vue';
import TranslationRestrictedTermsField, {
  loadStoredRestrictedTerms,
  saveStoredRestrictedTerms
} from './TranslationRestrictedTermsField.vue';
import { auditLocaleFileAgainstEnglish } from '@/plugins/locale-audit';
import { translateStrings } from '@/plugins/translate';
import { runWithConcurrency } from '@/plugins/concurrency';
import { formatTranslationDetail } from '@/plugins/translation-progress';
import Confirm from './Confirm.vue';
import AssetUrlBar from './AssetUrlBar.vue';
import SaveDiffModal from './SaveDiffModal.vue';
import TranslationPreviewPane from './TranslationPreviewPane.vue';
import TranslationModelSelect from './TranslationModelSelect.vue';
import EnglishReferencePane from './EnglishReferencePane.vue';
import MoveModal from '@/components/MoveModal';
import RenameModal from '@/components/RenameModal';

export default {
  props: {
    icons: Object,
    storage: String,
    path: String,
    endpoints: Object,
    axios: Function,
    refreshPending: Boolean
  },
  components: {
    AssetUrlBar,
    Confirm,
    SaveDiffModal,
    TranslationPreviewPane,
    TranslationModelSelect,
    TranslationCostHint,
    TranslationPromptField,
    TranslationRestrictedTermsField,
    EnglishReferencePane,
    MoveModal,
    RenameModal,
    vueJsonEditor
  },
  data() {
    return {
      items: [],
      filter: '',
      content: '',
      savedBaseline: '',
      editorPath: '',
      skipPathWatch: false,
      comparisonOpen: false,
      comparingEnglish: false,
      compareTrigger: 0,
      contentAtCompare: null,
      promptAtCompare: null,
      uiInfoAtCompare: null,
      comparePanesReady: 0,
      scrollLinkDetach: null,
      generateTargetLocales: [],
      foundGenerateTargetLocales: [],
      scanningGenerateLocales: false,
      generateLocaleScanId: 0,
      generatingTranslation: false,
      generateProgress: '',
      translateSpecificKeys: false,
      generateSelectedKeyPaths: [],
      translationRestrictedTerms: loadStoredRestrictedTerms(),
      translationUiInfoConfig: loadStoredTranslationUiInfoConfig(),
      translationAdvancedOpen: false,
      aiTranslating: false,
      aiTranslatingDetail: '',
      englishComparisonScore: null,
      englishComparisonDiff: null,
      accuracyAuditing: false,
      accuracyAuditProgress: '',
      accuracyAuditScores: {},
      selectedPaths: []
    };
  },
  computed: {
    ...mapState([
      'bucket',
      'keepEnglishFeatures'
    ]),
    ...mapGetters([
      'isTranslationEnabled',
      'isCompareTranslationEnabled',
      'translationModel',
      'translationModelLabel',
      'translationConfig',
      'selectedTranslationModel',
      'selectedTranslationModelConfig',
      'canCreate',
      'canUpdate',
      'canDelete'
    ]),
    dirs() {
      return this.items.filter(
        item => item.type === "dir" && item.basename.includes(this.filter)
      );
    },
    files() {
      return this.items.filter(
          item => item.type === "file" && item.basename.includes(this.filter)
      );
    },
    visibleListItems() {
      return this.files;
    },
    selectedCount() {
      return this.selectedListItems.length;
    },
    hasSelection() {
      return this.selectedCount > 0;
    },
    allListSelected() {
      const visible = this.visibleListItems;
      return visible.length > 0 && visible.every(item => this.isSelected(item.path));
    },
    someListSelected() {
      return this.visibleListItems.some(item => this.isSelected(item.path));
    },
    selectedListItems() {
      const selected = new Set(this.selectedPaths);
      return this.items.filter(item => item.type === 'file' && selected.has(item.path));
    },
    isDir() {
      return this.path[this.path.length - 1] === "/";
    },
    accuracyAuditPlan() {
      if (!this.isDir) {
        return { eligible: false, englishPath: null, targets: [] };
      }
      return folderAccuracyAuditPlan(this.items);
    },
    showAccuracyAuditButton() {
      return this.isCompareTranslationEnabled && this.accuracyAuditPlan.eligible;
    },
    accuracyAuditSummary() {
      const scores = Object.values(this.accuracyAuditScores).filter(entry => entry && !entry.error);
      if (!scores.length) return '';
      const avg =
        Math.round((scores.reduce((sum, entry) => sum + entry.percent, 0) / scores.length) * 10) /
        10;
      return `${scores.length} locale file(s) audited · avg ${avg}% vs en.json`;
    },
    isFile() {
      return !this.isDir;
    },
    fileName() {
      if (!this.path) return '';
      const parts = this.path.split('/');
      return parts[parts.length - 1] || this.path;
    },
    assetUrl() {
      if (!this.path || this.isDir) return '';
      return this.formUrl(this.path);
    },
    isImage() {
      return this.isFile && /\.(jpe?g|png|gif|svg|webp|bmp|ico)$/i.test(this.path);
    },
    isVideo() {
      return this.isFile && /\.(mp4|webm|ogv|mov|m4v)$/i.test(this.path);
    },
    isTextEditable() {
      return this.isFile && /\.(txt|xml|html|csv)$/i.test(this.path);
    },
    isJSON() {
      return this.isFile && /\.json$/i.test(this.path);
    },
    sourceLocale() {
      if (isSenJsonPath(this.path)) return 'sen';
      return localeFromPath(this.path) || '';
    },
    sourceLocaleLabel() {
      if (isSenJsonPath(this.path)) return 'SEN';
      return this.sourceLocale ? this.sourceLocale.toUpperCase() : 'Source';
    },
    englishReferencePath() {
      return englishPathFromLocalePath(this.path) || '';
    },
    comparisonStale() {
      if (!this.comparisonOpen || this.contentAtCompare === null) return false;
      return !editorContentEquals(this.content, this.contentAtCompare, true);
    },
    comparePreviewContent() {
      return this.contentAtCompare;
    },
    comparePanesRequired() {
      return this.isSenFile ? 1 : 2;
    },
    isEnglishLocaleFile() {
      return isEnglishLocale(this.sourceLocale);
    },
    isSenFile() {
      return isSenJsonPath(this.path);
    },
    showTranslationSplit() {
      return (
        (this.isTranslationEnabled || this.isCompareTranslationEnabled) &&
        this.isJSON &&
        !this.isEnglishLocaleFile &&
        (Boolean(this.sourceLocale) || this.isSenFile)
      );
    },
    showGenerateToolbar() {
      return this.isTranslationEnabled && this.isJSON && this.isEnglishLocaleFile;
    },
    targetLocaleOptions() {
      return [...COMMON_TARGET_LOCALES, SEN_GENERATE_OPTION];
    },
    generateTargetLocaleOptions() {
      if (!this.translateSpecificKeys) return this.targetLocaleOptions;
      const found = new Set(this.foundGenerateTargetLocales);
      return this.targetLocaleOptions.filter(item => found.has(item.value));
    },
    generateTargetLocalePlaceholder() {
      if (!this.translateSpecificKeys) return 'Select languages';
      if (this.scanningGenerateLocales) return 'Scanning existing locales…';
      if (!this.generateTargetLocaleOptions.length) return 'No existing locale files found';
      return 'Select existing locales';
    },
    allGenerateLocalesSelected() {
      const options = this.generateTargetLocaleOptions;
      return (
        options.length > 0 &&
        options.every(item => this.generateTargetLocales.includes(item.value))
      );
    },
    missingGenerateTargetLocales() {
      const found = new Set(this.foundGenerateTargetLocales);
      return this.targetLocaleOptions
        .filter(item => !found.has(item.value))
        .map(item => item.value);
    },
    allMissingGenerateLocalesSelected() {
      const missing = this.missingGenerateTargetLocales;
      if (!missing.length) return true;
      return missing.every(locale => this.generateTargetLocales.includes(locale));
    },
    canAddMissingGenerateLocales() {
      return (
        this.showGenerateToolbar &&
        !this.translateSpecificKeys &&
        !this.scanningGenerateLocales &&
        this.missingGenerateTargetLocales.length > 0
      );
    },
    translationStringLeaves() {
      if (typeof this.content !== 'object' || this.content === null || Array.isArray(this.content)) {
        return [];
      }
      return collectStringLeaves(this.content);
    },
    translationStringTexts() {
      return this.translationStringLeaves.map(entry => entry.value);
    },
    generateKeyPathOptions() {
      return this.translationStringLeaves.map(entry => {
        const value = String(entry.value || '').replace(/\s+/g, ' ').trim();
        const preview = value.length > 48 ? `${value.slice(0, 48)}…` : value;
        return {
          value: entry.path,
          text: preview ? `${entry.path} — ${preview}` : entry.path
        };
      });
    },
    canGenerateTranslations() {
      if (!this.isTranslationEnabled || this.generatingTranslation) return false;
      if (!this.canCreate && !this.canUpdate) return false;
      if (!this.generateTargetLocales.length) return false;
      if (this.translateSpecificKeys && !this.generateSelectedKeyPaths.length) return false;
      return true;
    },
    generateSelectedEntries() {
      if (!this.translateSpecificKeys) return [];
      const selectedPaths = normalizeSelectedKeyPaths(this.generateSelectedKeyPaths);
      if (!selectedPaths.length) return [];
      return filterEntriesByPaths(this.translationStringLeaves, selectedPaths);
    },
    generateTranslationCostEstimate() {
      const targetCount = this.generateTargetLocales.length;
      if (!targetCount || this.translationModel === 'off') return null;
      const selectedEntries = this.generateSelectedEntries;
      if (this.translateSpecificKeys && !selectedEntries.length) return null;
      const texts = this.translateSpecificKeys
        ? selectedEntries.map(entry => entry.value)
        : this.translationStringTexts;
      const characterCount = countTranslationCharacters(texts);
      if (!characterCount) return null;
      return estimateTranslationCost({
        characterCount,
        targetLocaleCount: targetCount,
        model: this.selectedTranslationModel,
        modelLabel: this.selectedTranslationModelConfig?.modelLabel,
        pricing: this.selectedTranslationModelConfig?.pricing,
        estimateChunkChars: this.selectedTranslationModelConfig?.estimateChunkChars
      });
    },
    singlePassTranslationEstimate() {
      if (!this.showTranslationSplit && !this.showGenerateToolbar) return null;
      const characterCount = countTranslationCharacters(this.translationStringTexts);
      if (!characterCount) return null;
      if (this.translationModel === 'off') return null;
      return estimateTranslationCost({
        characterCount,
        targetLocaleCount: 1,
        model: this.selectedTranslationModel,
        modelLabel: this.selectedTranslationModelConfig?.modelLabel,
        pricing: this.selectedTranslationModelConfig?.pricing,
        estimateChunkChars: this.selectedTranslationModelConfig?.estimateChunkChars
      });
    },
    fileViewClass() {
      return {
        'gc-file-view--media': this.isImage || this.isVideo,
        'gc-file-view--editor': this.isTextEditable || this.isJSON
      };
    },
    fileBodyClass() {
      return {
        'gc-editor-panel': this.isTextEditable || this.isJSON,
        'gc-json-editor': this.isJSON,
        'gc-json-editor--split': this.showTranslationSplit,
        'gc-json-editor--generate': this.showGenerateToolbar
      };
    },
    isEditable() {
      return (this.isTextEditable || this.isJSON) && this.canUpdate;
    },
    currentSerialized() {
      if (this.isJSON) return this.jsonEditorText();
      return serializeEditorContent(this.content, false);
    },
    hasUnsavedChanges() {
      if (!this.isEditable || this.editorPath !== this.path) return false;
      return !editorContentEquals(this.content, this.savedBaseline, this.isJSON);
    },
    showAiTranslationOverlay() {
      return this.aiTranslating || this.generatingTranslation;
    },
    aiOverlayMessage() {
      if (this.generatingTranslation) {
        return this.translateSpecificKeys
          ? 'Generating selected keys'
          : 'Generating translations';
      }
      return `Translating with ${this.translationModelLabel}`;
    },
    aiOverlayDetail() {
      if (this.generateProgress) return this.generateProgress;
      return this.aiTranslatingDetail;
    },
    aiOverlayState() {
      return {
        visible: this.showAiTranslationOverlay,
        message: this.aiOverlayMessage,
        detail: this.aiOverlayDetail
      };
    },
    englishAccuracyLevel() {
      if (!this.englishComparisonScore) return 'none';
      return accuracyLevel(this.englishComparisonScore.percent);
    },
    englishAccuracyLevelLabel() {
      return accuracyLevelLabel(this.englishAccuracyLevel);
    },
    englishAccuracyUnavailable() {
      if (!this.comparisonOpen || this.comparingEnglish || this.englishComparisonScore) {
        return '';
      }
      const preview = this.$refs.translationPreview;
      const reference = this.$refs.englishReference;
      if (this.isSenFile) {
        if (reference?.error) return reference.error;
        if (!reference?.hasContent) return 'en.json failed to load.';
        return '';
      }
      if (preview?.error) return preview.error;
      if (reference?.error) return reference.error;
      if (!preview?.hasPreview) return 'Machine EN preview not ready.';
      if (!reference?.hasContent) return 'en.json failed to load.';
      return '';
    },
    englishAccuracyTitle() {
      const s = this.englishComparisonScore;
      if (!s) return '';
      const enPath = this.englishReferencePath || 'en.json';
      const compareLabel = this.isSenFile
        ? 'sen.json vs en.json'
        : `Google Translate ${this.sourceLocaleLabel} → EN; word match vs en.json`;
      const parts = [
        `vs master ${enPath}.`,
        `${compareLabel}.`,
        `${s.percent}% (${s.matchedWords}/${s.totalWords} words).`,
        `Traffic light: Green ≥${ACCURACY_GREEN_MIN}%, Amber ${ACCURACY_AMBER_MIN}–${ACCURACY_GREEN_MIN - 1}%, Red <${ACCURACY_AMBER_MIN}%.`
      ];
      if (s.mismatched) parts.push(`${s.mismatched} keys with word differences.`);
      if (s.missingInMachine) {
        parts.push(`${s.missingInMachine} en.json keys missing from preview.`);
      }
      if (s.extraInMachine) {
        parts.push(`${s.extraInMachine} keys only in preview.`);
      }
      return parts.join(' ');
    }
  },
  methods: {
    formatBytes,
    formatFileMeta: buildFileMeta,
    onRestrictedTermsInput(value) {
      this.translationRestrictedTerms = Array.isArray(value) ? value : [];
      saveStoredRestrictedTerms(this.translationRestrictedTerms);
    },
    onTranslationUiInfoInput(value) {
      this.translationUiInfoConfig = value;
      saveStoredTranslationUiInfoConfig(value);
    },
    onAiTranslating(payload) {
      if (this.comparisonOpen) return;
      const active = Boolean(payload?.active);
      this.aiTranslating = active;
      this.aiTranslatingDetail = active ? payload?.detail || '' : '';
    },
    onTranslationCompleted() {
      /* Compare preview finished — keep restricted terms / UI info. */
    },
    selectAllGenerateLocales() {
      this.generateTargetLocales = this.generateTargetLocaleOptions.map(item => item.value);
    },
    addMissingGenerateLocales() {
      if (this.translateSpecificKeys) return;
      const merged = [
        ...new Set([...this.generateTargetLocales, ...this.missingGenerateTargetLocales])
      ];
      this.generateTargetLocales = merged;
    },
    clearGenerateLocales() {
      this.generateTargetLocales = [];
    },
    clearGenerateSelectedKeys() {
      this.generateSelectedKeyPaths = [];
    },
    pruneGenerateTargetLocalesToExisting() {
      const allowed = new Set(this.generateTargetLocaleOptions.map(item => item.value));
      this.generateTargetLocales = this.generateTargetLocales.filter(locale => allowed.has(locale));
    },
    onTranslateSpecificKeysChange(enabled) {
      if (!enabled) {
        this.generateSelectedKeyPaths = [];
        return;
      }
      this.pruneGenerateTargetLocalesToExisting();
      if (!this.foundGenerateTargetLocales.length && !this.scanningGenerateLocales) {
        this.refreshFoundGenerateTargetLocales().then(() => {
          this.pruneGenerateTargetLocalesToExisting();
        });
      }
    },
    onGenerateSelectedKeysChange(paths) {
      const unique = normalizeSelectedKeyPaths(paths, 21);
      if (unique.length > 20) {
        this.generateSelectedKeyPaths = unique.slice(0, 20);
        this.$toast.open({
          message: 'Maximum 20 keys per Generate run',
          type: 'warning',
          duration: 3500
        });
        return;
      }
      this.generateSelectedKeyPaths = unique;
    },
    resetAccuracyAudit() {
      this.accuracyAuditing = false;
      this.accuracyAuditProgress = '';
      this.accuracyAuditScores = {};
    },
    fileAuditScore(path) {
      if (!path) return null;
      const normalized = path.replace(/\/+$/, '');
      return (
        this.accuracyAuditScores[path] ||
        this.accuracyAuditScores[normalized] ||
        this.accuracyAuditScores[`${normalized}/`] ||
        null
      );
    },
    auditPathForListItem(item) {
      if (!item?.path) return null;
      if (item.type === 'file') return item.path.replace(/\/+$/, '');
      if (item.type === 'dir' && item.basename) {
        return `${item.path.replace(/\/+$/, '')}/${item.basename}.json`;
      }
      return null;
    },
    itemAuditScore(item) {
      const auditPath = this.auditPathForListItem(item);
      return auditPath ? this.fileAuditScore(auditPath) : null;
    },
    itemAuditTitle(item) {
      const auditPath = this.auditPathForListItem(item);
      return auditPath ? this.fileAuditTitle(auditPath) : '';
    },
    fileAuditTitle(path) {
      const score = this.fileAuditScore(path);
      if (!score) return '';
      if (score.error) return score.error;
      const label = score.label || accuracyLevelLabel(score.level);
      return [
        `${label}: ${score.percent}% word match vs en.json`,
        `${score.matchedWords}/${score.totalWords} words`,
        `Green ≥${ACCURACY_GREEN_MIN}%, Amber ${ACCURACY_AMBER_MIN}–${ACCURACY_GREEN_MIN - 1}%, Red <${ACCURACY_AMBER_MIN}%`
      ].join(' · ');
    },
    async fetchJsonPath(filePath) {
      const url = this.endpoints.get.url
        .replace(new RegExp('{storage}', 'g'), this.storage)
        .replace(new RegExp('{path}', 'g'), filePath);

      const res = await this.axios.request({
        url,
        method: this.endpoints.get.method || 'get'
      });

      let data = res.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          throw new Error(`Invalid JSON: ${filePath.split('/').pop()}`);
        }
      }
      return data;
    },
    async runAccuracyAudit() {
      const plan = this.accuracyAuditPlan;
      if (!plan.eligible || this.accuracyAuditing) return;

      this.accuracyAuditing = true;
      this.accuracyAuditScores = {};
      this.accuracyAuditProgress = 'Loading en.json…';

      try {
        const englishContent = await this.fetchJsonPath(plan.englishPath);
        const targets = plan.targets;
        let completed = 0;

        await runWithConcurrency(targets, 3, async target => {
          this.accuracyAuditProgress = `Auditing ${target.locale.toUpperCase()} (${completed + 1}/${targets.length})…`;
          try {
            const localeContent = await this.fetchJsonPath(target.path);
            const result = await auditLocaleFileAgainstEnglish({
              axios: this.axios,
              bucket: this.bucket,
              localePath: target.path,
              localeContent,
              englishContent
            });

            if (!result) {
              throw new Error('Could not score file');
            }

            const level = accuracyLevel(result.percent);
            this.$set(this.accuracyAuditScores, target.path, {
              percent: result.percent,
              level,
              matchedWords: result.matchedWords,
              totalWords: result.totalWords,
              locale: target.locale,
              label: accuracyLevelLabel(level)
            });
          } catch (e) {
            const status = e?.response?.status;
            if (status === 401) {
              throw e;
            }
            this.$set(this.accuracyAuditScores, target.path, {
              percent: null,
              level: 'none',
              error: e.response?.data?.message || e.message || 'Audit failed'
            });
          } finally {
            completed += 1;
          }
        });

        this.accuracyAuditProgress = '';
      } catch (e) {
        this.$toast.open({
          message: e.response?.data?.message || e.message || 'Accuracy audit failed',
          type: 'error',
          duration: 4500
        });
        this.accuracyAuditProgress = '';
      } finally {
        this.accuracyAuditing = false;
      }
    },
    async listStoragePath(path) {
      const url = this.endpoints.list.url
        .replace(new RegExp('{storage}', 'g'), this.storage)
        .replace(new RegExp('{path}', 'g'), path);

      const response = await this.axios.request({
        url,
        method: this.endpoints.list.method || 'get'
      });

      return Array.isArray(response.data) ? response.data : [];
    },
    async refreshFoundGenerateTargetLocales() {
      if (!this.showGenerateToolbar || !this.path) {
        this.foundGenerateTargetLocales = [];
        this.scanningGenerateLocales = false;
        return;
      }

      const discoveryPath = discoveryListPath(this.path);
      if (!discoveryPath) {
        this.foundGenerateTargetLocales = [];
        this.scanningGenerateLocales = false;
        return;
      }

      const scanId = ++this.generateLocaleScanId;
      this.scanningGenerateLocales = true;

      try {
        const rootItems = await this.listStoragePath(discoveryPath);
        if (scanId !== this.generateLocaleScanId) return;

        let allItems = [...rootItems];
        const subLists = nestedLocaleDirsToProbe(this.path, rootItems);
        if (subLists.length) {
          const nested = await Promise.all(subLists.map(path => this.listStoragePath(path)));
          if (scanId !== this.generateLocaleScanId) return;
          nested.forEach(items => {
            allItems = allItems.concat(items);
          });
        }

        this.foundGenerateTargetLocales = existingTargetLocalesFromListings(
          this.path,
          allItems
        );
      } catch (e) {
        if (scanId !== this.generateLocaleScanId) return;
        console.error(e);
        this.foundGenerateTargetLocales = [];
      } finally {
        if (scanId === this.generateLocaleScanId) {
          this.scanningGenerateLocales = false;
        }
      }
    },
    isImagePath: checkImagePath,
    formUrl(path, imageParse = false) {
      return imageUrl(this.bucket, path, imageParse);
    },
    async copyAssetUrl() {
      if (!this.assetUrl) return;
      try {
        await navigator.clipboard.writeText(this.assetUrl);
        this.$toast.open({
          message: 'URL copied to clipboard',
          type: 'success',
          duration: 2500
        });
      } catch {
        this.$toast.open({
          message: 'Could not copy URL',
          type: 'error',
          duration: 3000
        });
      }
    },
    isEditablePath(path) {
      return path && !path.endsWith('/') && /\.(txt|xml|html|csv|json)$/i.test(path);
    },

    jsonEditorText() {
      const fallback = serializeEditorContent(this.content, true);
      return readJsonEditorText(this.$refs.jsonEditor?.editor, fallback);
    },

    onTranslationPreviewError(message) {
      this.$toast.open({
        message: message || 'English preview unavailable',
        type: 'error',
        duration: 4000
      });
    },

    snapshotContentForCompare() {
      if (this.content === null || this.content === undefined) return this.content;
      if (typeof this.content !== 'object') return this.content;
      try {
        return JSON.parse(JSON.stringify(this.content));
      } catch {
        return this.content;
      }
    },

    resetEnglishComparison() {
      this.detachScrollSync();
      this.aiTranslating = false;
      this.aiTranslatingDetail = '';
      this.comparisonOpen = false;
      this.comparingEnglish = false;
      this.compareTrigger = 0;
      this.contentAtCompare = null;
      this.promptAtCompare = null;
      this.uiInfoAtCompare = null;
      this.comparePanesReady = 0;
      this.englishComparisonScore = null;
      this.englishComparisonDiff = null;
    },

    serializeEnglishComparisonDiff(diff) {
      if (!diff) return null;
      return {
        mismatched: Array.from(diff.mismatched.entries()),
        missingInMachine: Array.from(diff.missingInMachine),
        extraInMachine: Array.from(diff.extraInMachine)
      };
    },

    updateEnglishComparisonScore() {
      if (!this.comparisonOpen) {
        this.englishComparisonScore = null;
        this.englishComparisonDiff = null;
        return;
      }

      const preview = this.$refs.translationPreview;
      const reference = this.$refs.englishReference;

      if (!reference || reference.loading || reference.error || !reference.hasContent) {
        this.englishComparisonScore = null;
        this.englishComparisonDiff = null;
        return;
      }

      if (this.isSenFile) {
        if (
          this.contentAtCompare === null ||
          typeof this.contentAtCompare === 'string' ||
          typeof reference.referenceContent === 'string'
        ) {
          this.englishComparisonScore = null;
          this.englishComparisonDiff = null;
          return;
        }

        const diff = buildEnglishComparisonDiff(
          this.contentAtCompare,
          reference.referenceContent
        );
        this.englishComparisonDiff = this.serializeEnglishComparisonDiff(diff);
        this.englishComparisonScore = scoreEnglishComparison(
          this.contentAtCompare,
          reference.referenceContent
        );

        this.$nextTick(() => {
          reference.refreshCompareHighlights?.();
        });
        return;
      }

      if (!preview || preview.loading || preview.error || !preview.hasPreview) {
        this.englishComparisonScore = null;
        this.englishComparisonDiff = null;
        return;
      }

      if (
        typeof preview.englishPreview === 'string' ||
        typeof reference.referenceContent === 'string'
      ) {
        this.englishComparisonScore = null;
        this.englishComparisonDiff = null;
        return;
      }

      const diff = buildEnglishComparisonDiff(
        preview.englishPreview,
        reference.referenceContent
      );
      this.englishComparisonDiff = this.serializeEnglishComparisonDiff(diff);
      this.englishComparisonScore = scoreEnglishComparison(
        preview.englishPreview,
        reference.referenceContent
      );

      this.$nextTick(() => {
        preview.refreshCompareHighlights?.();
        reference.refreshCompareHighlights?.();
      });
    },

    detachScrollSync() {
      if (this.scrollLinkDetach) {
        this.scrollLinkDetach();
        this.scrollLinkDetach = null;
      }
    },

    async attachScrollSync(retries = 8) {
      this.detachScrollSync();
      await this.$nextTick();
      await this.$nextTick();

      const elements = [
        getJsonEditorScrollElement(this.$refs.jsonEditor),
        this.$refs.translationPreview?.getScrollElement?.(),
        this.$refs.englishReference?.getScrollElement?.()
      ].filter(Boolean);

      if (elements.length < 2 && retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 150));
        return this.attachScrollSync(retries - 1);
      }

      const { detach } = linkJsonEditorScroll(elements);
      this.scrollLinkDetach = detach;
    },

    onComparePaneReady() {
      this.comparePanesReady += 1;
      if (this.comparePanesReady >= this.comparePanesRequired) {
        this.comparingEnglish = false;
        this.attachScrollSync();
      }
      this.$nextTick(() => this.updateEnglishComparisonScore());
    },

    async runEnglishComparison() {
      if (!this.isCompareTranslationEnabled || this.comparingEnglish) return;

      this.contentAtCompare = this.snapshotContentForCompare();
      this.promptAtCompare = '';
      this.uiInfoAtCompare = this.translationUiInfoConfig;
      this.comparisonOpen = true;
      this.comparingEnglish = true;
      this.comparePanesReady = 0;
      this.detachScrollSync();

      await this.$nextTick();
      this.compareTrigger += 1;
    },

    async refreshEnglishComparison() {
      if (!this.comparisonOpen || this.comparingEnglish) return;

      this.contentAtCompare = this.snapshotContentForCompare();
      this.promptAtCompare = '';
      this.uiInfoAtCompare = this.translationUiInfoConfig;
      this.comparingEnglish = true;
      this.comparePanesReady = 0;
      this.detachScrollSync();

      if (this.isSenFile) {
        const reference = this.$refs.englishReference;
        if (reference?.loadReference) {
          await reference.loadReference();
        }
      } else {
        const preview = this.$refs.translationPreview;
        if (preview?.refreshPreview) {
          await preview.refreshPreview();
        }
      }
      this.comparingEnglish = false;
      await this.attachScrollSync();
      this.updateEnglishComparisonScore();
    },

    async whenJsonEditorReady(callback) {
      await this.$nextTick();
      await this.$nextTick();
      callback();
    },

    syncEditorBaseline() {
      if (!this.isEditable) {
        this.editorPath = '';
        this.savedBaseline = '';
        return;
      }
      this.editorPath = this.path;
      if (this.isJSON) {
        this.whenJsonEditorReady(() => {
          if (this.editorPath === this.path) {
            this.savedBaseline = this.jsonEditorText();
          }
        });
      } else {
        this.savedBaseline = serializeEditorContent(this.content, false);
      }
    },

    async confirmDiscardChanges() {
      return this.$refs.confirm.open(
        'Unsaved changes',
        'You have unsaved changes in this file.<br>Discard them and continue?',
        { color: 'error', width: 440 }
      );
    },

    async guardUnsavedForPath(path) {
      if (path !== this.editorPath || !this.isEditablePath(path)) return true;
      if (!this.hasUnsavedChanges) return true;
      return this.confirmDiscardChanges();
    },

    changePath(path) {
      if (path === this.path) return;
      this.$emit('path-changed', path);
    },
    isSelected(path) {
      return this.selectedPaths.includes(path);
    },
    toggleSelect(item) {
      if (item.type !== 'file') return;
      const path = item.path;
      const idx = this.selectedPaths.indexOf(path);
      if (idx >= 0) {
        this.selectedPaths.splice(idx, 1);
      } else {
        this.selectedPaths.push(path);
      }
    },
    toggleSelectAll(selected) {
      if (selected) {
        const paths = this.visibleListItems.map(item => item.path);
        this.selectedPaths = [...new Set([...this.selectedPaths, ...paths])];
      } else {
        const visible = new Set(this.visibleListItems.map(item => item.path));
        this.selectedPaths = this.selectedPaths.filter(path => !visible.has(path));
      }
    },
    clearSelection() {
      this.selectedPaths = [];
    },
    async performDeletePath(itemPath) {
      const url = this.endpoints.delete.url
        .replace(new RegExp('{storage}', 'g'), this.storage)
        .replace(new RegExp('{path}', 'g'), itemPath);

      await this.axios.request({
        url,
        method: this.endpoints.delete.method || 'post'
      });
    },
    async deleteSelected() {
      const selected = this.selectedListItems;
      if (!selected.length) return;

      const fileList = selected.map(item => `<em>${item.basename}</em>`).join(', ');
      const confirmed = await this.$refs.confirm.open(
        'Delete selected',
        selected.length === 1
          ? `Delete ${fileList}?`
          : `Delete ${selected.length} items?<br>${fileList}`,
        { color: 'error', width: 480 }
      );
      if (!confirmed) return;

      try {
        this.$emit('loading', true);
        for (const item of selected) {
          if (item.path.length >= 2) {
            await this.performDeletePath(item.path);
          }
        }
        this.clearSelection();
        await this.load();
        this.$emit('refresh');
        this.$toast.open({
          message: selected.length === 1 ? '✓ Item deleted' : `✓ Deleted ${selected.length} items`,
          type: 'success',
          duration: 3500
        });
      } catch (e) {
        console.error(e);
        this.$toast.open({
          message: e.response?.data?.message || e.message || 'Delete failed',
          type: 'error',
          duration: 4500
        });
      } finally {
        this.$emit('loading', false);
      }
    },
    moveSelected() {
      if (!this.selectedListItems.length) return;
      this.$emit('bulk-move', this.selectedListItems);
    },
    async bulkMoveItems(selected, folderOptions) {
      const movable = selected.filter(item => item.type === 'file');
      if (!movable.length) return;

      const folders = folderOptions || [];
      const first = movable[0];
      const defaultPath = first.path.substring(0, first.path.lastIndexOf('/') + 1) || '/';
      const title = movable.length === 1
        ? first.basename
        : `${movable.length} items`;

      const destFolder = await this.$refs.moveModal.open(first.path, folders, {
        title,
        defaultPath
      });
      if (!destFolder) return;

      try {
        this.$emit('loading', true);
        for (const item of movable) {
          const basename = item.path.split(/[\\/]/).pop();
          const to = `${destFolder}${basename}`;
          if (item.path !== to) {
            await this.performMove(item.path, to, { navigate: false, manageLoading: false });
          }
        }
        this.clearSelection();
        await this.load();
        this.$emit('refresh');
        this.$toast.open({
          message: movable.length === 1 ? '✓ Item moved' : `✓ Moved ${movable.length} items`,
          type: 'success',
          duration: 3500
        });
      } catch (e) {
        console.error(e);
        this.$toast.open({
          message: e.response?.data?.message || e.message || 'Move failed',
          type: 'error',
          duration: 4500
        });
      } finally {
        this.$emit('loading', false);
      }
    },
    async load() {
      try {
        this.$emit("loading", true);

        if (this.isDir) {
          let url = this.endpoints.list.url
              .replace(new RegExp("{storage}", "g"), this.storage)
              .replace(new RegExp("{path}", "g"), this.path);

          let config = {
            url,
            method: this.endpoints.list.method || "get"
          };

          let response = await this.axios.request(config);
          this.items = response.data;
          this.editorPath = '';
          this.savedBaseline = '';
        } else {
          if (this.path.match(/\.(txt|xml|html|csv|json)$/i)) {
            const url = this.endpoints.get.url
                .replace(new RegExp('{storage}', 'g'), this.storage)
                .replace(new RegExp('{path}', 'g'), this.path);

            const res = await this.axios.request({
              url,
              method: this.endpoints.get.method || 'get'
            });
            let data = res.data;
            if (this.isJSON && typeof data === 'string') {
              try {
                data = JSON.parse(data);
              } catch {
                /* editor shows raw string */
              }
            }
            this.content = data;
            this.syncEditorBaseline();
          }
        }

      } catch (e) {
        console.error(e);
      } finally {
        this.$emit('loading', false);
        if (this.showGenerateToolbar) {
          this.refreshFoundGenerateTargetLocales();
        } else {
          this.foundGenerateTargetLocales = [];
          this.scanningGenerateLocales = false;
        }
      }
    },
    async save() {
      if (!this.isEditable) return;

      const current = this.currentSerialized;
      if (!this.hasUnsavedChanges) {
        this.$toast.open({
          message: 'No changes to save',
          type: 'success',
          duration: 2500
        });
        return;
      }

      const before = this.isJSON
        ? formatJsonTextForDiff(this.savedBaseline)
        : this.savedBaseline;
      const confirmed = await this.$refs.saveDiff.open(this.fileName, before, current);
      if (!confirmed) return;

      await this.performSave();
    },

    async generateTargetTranslation() {
      if (!this.canGenerateTranslations) return;

      let selected = [...new Set(this.generateTargetLocales)].filter(Boolean);
      if (this.translateSpecificKeys) {
        const allowed = new Set(this.foundGenerateTargetLocales);
        selected = selected.filter(locale => allowed.has(locale));
        if (!selected.length) {
          this.$toast.open({
            message: 'Select at least one existing locale file',
            type: 'warning',
            duration: 3500
          });
          return;
        }
      }

      const wantsSen = selected.includes(SEN_JSON);
      const locales = selected.filter(
        value => value !== SEN_JSON && isTranslatableLocale(value)
      );
      if (!wantsSen && !locales.length) return;

      if (typeof this.content !== 'object' || this.content === null || Array.isArray(this.content)) {
        this.$toast.open({
          message: 'English content must be a JSON object',
          type: 'error',
          duration: 3500
        });
        return;
      }

      const selectedPaths = this.translateSpecificKeys
        ? normalizeSelectedKeyPaths(this.generateSelectedKeyPaths)
        : [];
      const allEntries = this.translationStringLeaves;
      let entries;
      let mergeIntoExisting = false;

      if (this.translateSpecificKeys) {
        if (!selectedPaths.length) {
          this.$toast.open({
            message: 'Select at least one key path, or turn off Translate specific keys',
            type: 'warning',
            duration: 3500
          });
          return;
        }
        entries = filterEntriesByPaths(allEntries, selectedPaths);
        if (!entries.length) {
          this.$toast.open({
            message: 'Selected paths are not string leaves in this EN file',
            type: 'error',
            duration: 4000
          });
          return;
        }
        mergeIntoExisting = true;
      } else {
        entries = allEntries;
      }

      const plans = this.buildGenerateLocalePlans(selected, wantsSen, locales);
      if (!plans) return;

      try {
        const fileList = plans.map(p => `<em>${p.name}</em>`).join(', ');
        const keyNote = mergeIntoExisting
          ? `Only ${entries.length} selected key(s) will be translated and merged; other keys stay unchanged.`
          : plans.length === 1
            ? 'This will overwrite the file if it already exists.'
            : 'This will overwrite any files that already exist.';
        const confirmed = await this.$refs.confirm.open(
          'Generate translations',
          plans.length === 1
            ? `Save machine translation from English to ${fileList}? ${keyNote}`
            : `Save machine translations from English to ${plans.length} files (${fileList})? ${keyNote}`,
          { color: 'error', width: 520 }
        );
        if (!confirmed) return;

        this.generatingTranslation = true;
        for (const entry of entries) {
          const live = getValueAtPath(this.content, entry.path);
          if (typeof live === 'string') entry.value = live;
        }
        const texts = entries.map(e => e.value);
        const pathList = entries.map(e => e.path);
        await this.runLocaleTranslationJob({
          plans,
          entries,
          texts,
          pathList,
          mergeIntoExisting
        });
      } catch (e) {
        this.$toast.open({
          message: e.response?.data?.message || e.message || 'Translation failed',
          type: 'error',
          duration: 4500
        });
      } finally {
        this.generatingTranslation = false;
        this.generateProgress = '';
      }
    },

    buildGenerateLocalePlans(selected, wantsSen, locales) {
      const plans = [];

      if (wantsSen) {
        const senPath = senPathFromEnglishPath(this.path);
        if (senPath) {
          plans.push({ isSen: true, path: senPath, name: SEN_JSON });
        }
      }

      for (const locale of locales) {
        const path = targetPathForLocale(this.path, locale);
        if (path) {
          plans.push({ locale, path, name: path.split('/').pop() });
        }
      }

      if (!plans.length) {
        this.$toast.open({
          message: 'Could not resolve target file paths for the selected languages',
          type: 'error',
          duration: 4000
        });
        return null;
      }

      if (plans.length < selected.length) {
        this.$toast.open({
          message: 'Some selected targets could not be resolved to file paths and were skipped',
          type: 'warning',
          duration: 4000
        });
      }

      return plans;
    },

    async loadLocaleJsonForMerge(filePath) {
      try {
        const data = await this.fetchJsonPath(filePath);
        if (data !== null && typeof data === 'object' && !Array.isArray(data)) {
          return data;
        }
        return {};
      } catch (e) {
        const status = e?.response?.status;
        if (status === 404 || status === 400) {
          return {};
        }
        throw e;
      }
    },

    async runLocaleTranslationJob({ plans, entries, texts, pathList, mergeIntoExisting }) {
      const saved = [];
      const failed = [];
      const localeConcurrency = Math.max(
        1,
        Number(this.translationConfig?.localeConcurrency) || 4
      );
      let completedLocales = 0;

      this.generateProgress = formatTranslationDetail({
        localeLabel: this.generatePlanLabel(plans[0]),
        stepIndex: plans.length ? 1 : 0,
        stepTotal: plans.length,
        stringCount: texts.length,
        modelLabel: this.translationModelLabel,
        extra:
          plans.length > 1 && localeConcurrency > 1
            ? `Up to ${localeConcurrency} locales at a time`
            : mergeIntoExisting
              ? 'Merging selected keys'
              : ''
      });

      const translationBase = {
        bucket: this.bucket,
        paths: pathList,
        uiInfoConfig: this.translationUiInfoConfig,
        restrictedTerms: this.translationRestrictedTerms,
        model: this.selectedTranslationModel
      };

      const translateResults = await runWithConcurrency(
        plans,
        localeConcurrency,
        async (plan, index) => {
          const localeLabel = this.generatePlanLabel(plan);
          this.generateProgress = formatTranslationDetail({
            localeLabel,
            stepIndex: index + 1,
            stepTotal: plans.length,
            stringCount: texts.length,
            modelLabel: this.translationModelLabel,
            extra:
              plans.length > 1 && localeConcurrency > 1
                ? `Up to ${localeConcurrency} locales at a time`
                : mergeIntoExisting
                  ? 'Merging selected keys'
                  : ''
          });

          try {
            const translations = await translateStrings(
              this.axios,
              texts,
              plan.isSen
                ? {
                    ...translationBase,
                    source: 'en',
                    target: 'sen'
                  }
                : {
                    ...translationBase,
                    source: 'en',
                    target: plan.locale
                  }
            );

            let translated;
            if (!entries.length) {
              translated = mergeIntoExisting
                ? await this.loadLocaleJsonForMerge(plan.path)
                : this.content;
            } else if (mergeIntoExisting) {
              const existing = await this.loadLocaleJsonForMerge(plan.path);
              translated = mergeTranslations(existing, entries, translations);
            } else {
              translated = applyTranslations(this.content, entries, translations);
            }

            completedLocales += 1;
            this.generateProgress = formatTranslationDetail({
              localeLabel,
              stepIndex: completedLocales,
              stepTotal: plans.length,
              stringCount: texts.length,
              modelLabel: this.translationModelLabel,
              extra:
                completedLocales < plans.length
                  ? `${completedLocales}/${plans.length} complete`
                  : 'Saving…'
            });

            return { ok: true, plan, translated };
          } catch (e) {
            const status = e?.response?.status;
            if (status === 401) {
              throw e;
            }

            completedLocales += 1;
            this.generateProgress = formatTranslationDetail({
              localeLabel,
              stepIndex: completedLocales,
              stepTotal: plans.length,
              stringCount: texts.length,
              modelLabel: this.translationModelLabel,
              extra:
                completedLocales < plans.length
                  ? `${completedLocales}/${plans.length} complete`
                  : 'Saving…'
            });
            return {
              ok: false,
              plan,
              message: e.response?.data?.message || e.message || 'Translation failed'
            };
          }
        }
      );

      this.generateProgress = formatTranslationDetail({
        stringCount: texts.length,
        modelLabel: this.translationModelLabel,
        extra: `Saving ${translateResults.filter(r => r.ok).length} file(s)`
      });

      for (const result of translateResults) {
        if (!result.ok) {
          failed.push({ plan: result.plan, message: result.message });
          continue;
        }

        try {
          await this.performSaveToPath(
            result.plan.path,
            stringifyJsonObject(result.translated)
          );
          saved.push(result.plan);
        } catch (e) {
          if (e?.response?.status === 401) {
            throw e;
          }
          failed.push({
            plan: result.plan,
            message: e.response?.data?.message || e.message || 'Save failed'
          });
        }
      }

      if (saved.length) {
        this.$emit('refresh', saved.map(p => p.path));
        await this.refreshFoundGenerateTargetLocales();
      }

      this.showGenerateTranslationResults(saved, failed);

      if (failed.length) {
        this.generateTargetLocales = failed.map(entry =>
          entry.plan.isSen ? SEN_JSON : entry.plan.locale
        );
      } else if (!mergeIntoExisting) {
        this.generateTargetLocales = [];
      } else {
        this.generateSelectedKeyPaths = [];
      }
    },

    generateLocaleLabel(locale) {
      const code = String(locale || '').trim();
      const match = this.targetLocaleOptions.find(item => item.value === code);
      return match?.text || code || 'unknown';
    },

    generatePlanLabel(plan) {
      if (!plan) return 'unknown';
      if (plan.isSen || plan.name === SEN_JSON) return 'Social English (sen.json)';
      return this.generateLocaleLabel(plan.locale);
    },

    showGenerateTranslationResults(saved, failed) {
      const savedNames = saved.map(p => p.name).join(', ');
      const failedLines = failed.map(
        entry => `${this.generatePlanLabel(entry.plan)}: ${entry.message}`
      );

      if (saved.length && !failed.length) {
        this.$toast.open({
          message:
            saved.length === 1
              ? `✓ Saved ${savedNames}`
              : `✓ Saved ${saved.length} files: ${savedNames}`,
          type: 'success',
          duration: saved.length > 1 ? 5000 : 3500
        });
        return;
      }

      if (saved.length && failed.length) {
        this.$toast.open({
          message: `Saved ${saved.length} file${saved.length === 1 ? '' : 's'} (${savedNames}). Failed: ${failedLines.join('; ')}`,
          type: 'warning',
          duration: 8000
        });
        return;
      }

      if (!saved.length && failed.length) {
        const prefix =
          failed.length === 1 ? 'Translation failed' : `All ${failed.length} translations failed`;
        this.$toast.open({
          message: `${prefix}: ${failedLines.join('; ')}`,
          type: 'error',
          duration: 8000
        });
      }
    },

    async performSaveToPath(filePath, contentPayload) {
      const url = this.endpoints.upload.url
        .replace(new RegExp('{storage}', 'g'), this.storage)
        .replace(new RegExp('{path}', 'g'), filePath);

      await this.axios.post(url, { content: contentPayload });
    },

    async performSave() {
      try {
        const payload = this.isJSON ? this.currentSerialized : this.content;
        await this.performSaveToPath(this.path, payload);
        this.syncEditorBaseline();
        this.$toast.open({
          message: '✓ Changes saved',
          type: 'success'
        });
      } catch (e) {
        this.$toast.open({
          message: 'Something went wrong!',
          type: 'error'
        });
      }
    },
    async deleteItem(item) {
      const itemType = item.type === 'dir' ? 'folder' : 'file';
      let confirmed = await this.$refs.confirm.open(
          'Delete',
          `Are you sure<br>you want to delete this ${itemType}?<br><em>${item.basename}</em>`
      );

      try {
        if (confirmed) {
          this.$emit('loading', true);

          let path = item.path;
          if (path.length < 2) return;

          await this.performDeletePath(item.path);

          if (this.isSelected(item.path)) {
            this.selectedPaths = this.selectedPaths.filter(p => p !== item.path);
          }

          if (this.isDir && path.charAt(path.length - 1) === '/') {
            path = path.slice(0, -1);
          }

          setTimeout(() => {
            this.changePath(path.substring(0, path.lastIndexOf('/')) + '/');
            this.$emit('refresh');
          }, 500);
        }
      } catch (e) {
          console.error(e);
          this.$toast.open({
            message: e.response?.data?.message || e.message || 'Delete failed',
            type: 'error',
            duration: 4500
          });
      } finally {
        this.$emit('loading', false);
      }
    },
    async rename(path) {
      const basename = path.split(/[\\/]/).pop();
      const newName = await this.$refs.renameModal.open(basename);

      if (newName) {
        await this.performMove(path, path.replace(basename, newName));
      }
    },
    async move(path, items) {
      items = this.filterFolders(items);

      let newPath = await this.$refs.moveModal.open(path, items);
      if (!newPath) return;

      newPath += path.split(/[\\/]/).pop();

      if (path !== newPath) {
        await this.performMove(path, newPath);
      }
    },
    async performMove(from, to, { navigate = true, manageLoading = true } = {}) {
      if (manageLoading) this.$emit('loading', true);

      try {
        const url = this.endpoints.move.url
          .replace(new RegExp('{storage}', 'g'), this.storage);

        await this.axios.post(url, { from, to });

        if (navigate) {
          const oldPath = from.substring(0, from.lastIndexOf('/') + 1);
          const newPath = to.substring(0, to.lastIndexOf('/') + 1);

          setTimeout(() => {
            this.changePath(oldPath.length < newPath.length ? oldPath : newPath);
            this.$emit('refresh');
          }, 500);
        }
      } catch (e) {
          console.error(e);
          throw e;
      } finally {
        if (manageLoading) this.$emit('loading', false);
      }
    },
    filterFolders(items, filtered = []) {
      for (const item of items) {
        if (item.type === 'dir') {
          filtered.push(item.path);
        }

        if (item.children && item.children.length) {
          this.filterFolders(item.children, filtered);
        }
      }

      return filtered;
    }
  },
  mounted() {
    this._onBeforeUnload = e => {
      if (this.hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    this._onKeydown = e => {
      if (!(e.ctrlKey || e.metaKey) || e.key !== 's') return;
      if (!this.isEditable || !this.hasUnsavedChanges) return;
      e.preventDefault();
      this.save();
    };
    window.addEventListener('beforeunload', this._onBeforeUnload);
    window.addEventListener('keydown', this._onKeydown);
  },

  beforeDestroy() {
    this.detachScrollSync();
    window.removeEventListener('beforeunload', this._onBeforeUnload);
    window.removeEventListener('keydown', this._onKeydown);
    this.$store.commit('setAiTranslationOverlay', { visible: false });
  },

  watch: {
    aiOverlayState: {
      deep: true,
      immediate: true,
      handler(state) {
        this.$store.commit('setAiTranslationOverlay', state);
      }
    },
    foundGenerateTargetLocales() {
      if (this.translateSpecificKeys) {
        this.pruneGenerateTargetLocalesToExisting();
      }
    },
    async path(newPath, oldPath) {
      if (this.skipPathWatch) {
        this.skipPathWatch = false;
        this.items = [];
        await this.load();
        return;
      }

      if (oldPath && (await this.guardUnsavedForPath(oldPath)) === false) {
        this.skipPathWatch = true;
        this.$emit('path-changed', oldPath);
        return;
      }

      this.resetEnglishComparison();
      this.resetAccuracyAudit();
      this.clearSelection();
      this.items = [];
      await this.load();
    },
    async refreshPending() {
      if (this.refreshPending) {
        await this.load();
        this.$emit("refreshed");
      }
    }
  }
};
</script>

<style lang="scss">
@import '@/assets/styles/base/brand';

.gc-list-panel.v-card {
  height: 100%;
}

.save-editor-btn {
  position: relative;

  &__dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-left: 8px;
    border-radius: 50%;
    background: $gc-gold;
    vertical-align: middle;
  }
}

.gc-locale-audit-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba($gc-sky-blue, 0.25);
  background: rgba($gc-navy, 0.45);

  &__btn {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 600;
  }

  &__progress,
  &__hint {
    font-size: 12px;
    color: rgba($gc-white, 0.72);
    line-height: 1.35;
  }
}

.gc-list-item__audit-wrap {
  min-width: auto !important;
  margin-right: 4px;
}

.gc-list-item__audit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  border: 1px solid transparent;

  &--green {
    color: #4ade80;
    background: rgba(#4ade80, 0.12);
    border-color: rgba(#4ade80, 0.35);
  }

  &--amber {
    color: #fbbf24;
    background: rgba(#fbbf24, 0.14);
    border-color: rgba(#fbbf24, 0.4);
  }

  &--red {
    color: #f87171;
    background: rgba(#f87171, 0.1);
    border-color: rgba(#f87171, 0.35);
  }

  &--none {
    color: rgba($gc-white, 0.55);
    background: rgba($gc-white, 0.06);
    border-color: rgba($gc-white, 0.12);
    font-weight: 600;
  }
}
</style>